import { Alert, Button, Spinner } from '@personachat/ui';
import { useQuery } from '@tanstack/react-query';
import { Bot, Check, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import { useSession } from '../../auth/AuthProvider';

/** Adresse de retour sur cet ordinateur : application installée (Claude Code, Claude Desktop). */
export function retourLocal(hote: string): boolean {
  return /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/.test(hote);
}

/**
 * Page « Autoriser » du MCP : une application (Claude…) demande à agir sur le
 * back-office avec le compte de l'admin connecté. On y arrive depuis
 * /oauth/authorize ; l'accord renvoie l'admin vers l'application.
 */
export function AutorisationMcpPage() {
  const [params] = useSearchParams();
  const demande = params.get('demande') ?? '';
  const { user } = useSession();
  const [envoi, setEnvoi] = useState<'approuver' | 'refuser' | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const infos = useQuery({
    queryKey: ['mcp-demande', demande],
    queryFn: () => lire(api.GET('/api/admin/mcp/demande', { params: { query: { d: demande } } })),
    enabled: !!demande,
    retry: false,
  });

  const repondre = async (choix: 'approuver' | 'refuser') => {
    setEnvoi(choix);
    setErreur(null);
    try {
      const r =
        choix === 'approuver'
          ? await lire(api.POST('/api/admin/mcp/demande/approuver', { body: { demande } }))
          : await lire(api.POST('/api/admin/mcp/demande/refuser', { body: { demande } }));
      window.location.assign(r.redirectUrl);
    } catch (err) {
      setErreur(messageDe(err));
      setEnvoi(null);
    }
  };

  const i = infos.data;
  const local = i ? retourLocal(i.redirectHost) : false;

  return (
    <div className="login">
      <div className="login__card login__card--large">
        <div className="login__brand">
          <span className="sidebar__logo" aria-hidden="true">
            M
          </span>
          <div>
            <h1>Back-office</h1>
            <p>Connexion d’un assistant IA</p>
          </div>
        </div>

        {!demande ? (
          <Alert>Lien incomplet : relancez la connexion depuis l’application.</Alert>
        ) : infos.isLoading ? (
          <Spinner label="Vérification de la demande" />
        ) : infos.isError || !i ? (
          <>
            <Alert>{messageDe(infos.error)}</Alert>
            <Link className="lien-discret" to="/">
              Aller au back-office
            </Link>
          </>
        ) : (
          <>
            <div className="mcp-demande">
              <Bot size={22} aria-hidden="true" />
              <p>
                <strong>{i.application.name}</strong> demande à utiliser le back-office avec votre compte <strong>{user?.email}</strong>.
              </p>
            </div>
            <div className="stack" style={{ gap: 6 }}>
              <p className="texte-aide">Une fois autorisé, il pourra :</p>
              <ul className="liste-aide">
                <li>consulter les élèves, leurs crédits et leurs coachs, les clés, les ventes, les analytics et le journal d’audit ;</li>
                <li>
                  agir comme vous : ajouter ou retirer des crédits, débloquer un coach, changer un type de compte, gérer les clés,
                  renvoyer un e-mail. Toujours avec un motif, inscrit au journal avec la mention « MCP ».
                </li>
              </ul>
              <p className="texte-aide">
                Il n’aura jamais plus de droits que vous. Ni suppression, ni remboursement, ni lecture des conversations ou des mémoires.
                Vous pourrez retirer cet accès à tout moment depuis « Mon compte ».
              </p>
            </div>
            <p className="note" style={{ margin: 0 }}>
              L’autorisation sera envoyée à <strong>{i.redirectHost}</strong>
              {local ? ' (une application installée sur cet ordinateur, comme Claude Code ou Claude Desktop)' : ''}. Refusez si vous n’êtes pas à
              l’origine de cette demande.
            </p>
            {erreur && <Alert>{erreur}</Alert>}
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Button icon={<X size={15} aria-hidden="true" />} onClick={() => void repondre('refuser')} loading={envoi === 'refuser'} disabled={!!envoi}>
                Refuser
              </Button>
              <Button
                variant="primary"
                icon={<Check size={15} aria-hidden="true" />}
                onClick={() => void repondre('approuver')}
                loading={envoi === 'approuver'}
                disabled={!!envoi}
              >
                Autoriser
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
